import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Card, H4, Icon, P1, P2, useWindowSize } from '@moneymade/moneymade-ui'
import { useTransition, animated } from 'react-spring'

import KnowPointsCard from 'modules/asset/AssetPage/Highlights/KnowPointsCard/KnowPointsCard'
import InfoModal from 'modules/asset/AssetPage/InfoModal/InfoModal'

import styles from './Highlights.module.scss'

const Highlights = ({
  title,
  goodFor,
  time,
  earnings,
  defaultRate,
  liquidity,
  diversification,
  environmentally,
  text,
  didYouKnow
}) => {
  const [isDiversificationModal, setDiversificationModal] = useState(false)
  const [isEnvironmentallyModal, setEnvironmentallyModal] = useState(false)
  const [isLiquidityModal, setLiquidityModal] = useState(false)
  const { screenWidth } = useWindowSize()

  const [showMore, setShowMore] = useState(true)

  useEffect(() => {
    if (screenWidth > 601 && !showMore) {
      setShowMore(true)
    } else if (screenWidth !== 0 && showMore) {
      setShowMore(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenWidth])

  const transition = useTransition(showMore, {
    from: { transform: 'translateY(-20px)', opacity: 0 },
    enter: { transform: 'translateY(0)', opacity: 1 },
    leave: { transform: 'translateY(-20px)', opacity: 0 },
    config: { duration: 200 }
  })

  return (
    <>
      {diversification.title && (
        <InfoModal
          isOpen={isDiversificationModal}
          data={diversification}
          handleClose={() => setDiversificationModal(false)}
        />
      )}
      {environmentally.title && (
        <InfoModal
          isOpen={isEnvironmentallyModal}
          data={environmentally}
          handleClose={() => setEnvironmentallyModal(false)}
        />
      )}
      {liquidity.title && (
        <InfoModal isOpen={isLiquidityModal} data={liquidity} handleClose={() => setLiquidityModal(false)} />
      )}

      <Card className={styles.Highlights}>
        <div className={`${styles.Header} ${showMore ? styles.isOpen : ''}`}>
          {title && (
            <H4 weight="semibold " className={styles.Title}>
              {title}
            </H4>
          )}
          <button type="button" className={styles.Heading} onClick={() => setShowMore(prev => !prev)}>
            {showMore ? <Icon icon="icon-point-up" size={18} /> : <Icon icon="icon-point-down" size={18} />}
          </button>
        </div>

        {transition(
          (springStyles, item) =>
            item && (
              <animated.div style={springStyles} className={styles.Card}>
                <div className={styles.Content}>
                  <div className={styles.LeftSide}>
                    <div className={styles.Blocks}>
                      {goodFor && (
                        <div className={styles.Block}>
                          <P2>Good For</P2>
                          <P1 weight="normal">{goodFor}</P1>
                        </div>
                      )}

                      {time && (
                        <div className={styles.Block}>
                          <P2>Time Horizon</P2>
                          <P1 weight="normal">{time}</P1>
                        </div>
                      )}

                      {earnings && (
                        <div className={styles.Block}>
                          <P2>Earnings from</P2>
                          <P1 weight="normal">{earnings}</P1>
                        </div>
                      )}

                      {defaultRate && (
                        <div className={styles.Block}>
                          <P2>Default Rate</P2>
                          <P1 weight="normal">{defaultRate}</P1>
                        </div>
                      )}

                      {liquidity?.value && (
                        <div className={styles.Block}>
                          <P2>
                            Liquidity
                            <button type="button" className={styles.InfoBtn} onClick={() => setLiquidityModal(true)}>
                              <Icon icon="icon-info" size={14} />
                            </button>
                          </P2>
                          <P1 weight="normal">{liquidity.value}</P1>
                        </div>
                      )}

                      {Number.isInteger(diversification?.value) && (
                        <div className={styles.Block}>
                          <P2>
                            Diversification
                            <button
                              type="button"
                              className={styles.InfoBtn}
                              onClick={() => setDiversificationModal(true)}
                            >
                              <Icon icon="icon-info" size={14} />
                            </button>
                          </P2>
                          <div className={styles.Scale}>
                            {[...Array(5)].map((_, index) => (
                              <span
                                key={`scale-${index}`}
                                className={diversification.value >= index + 1 ? styles.Active : ''}
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {Number.isInteger(environmentally?.value) && (
                        <div className={styles.Block}>
                          <P2>
                            Environmentally Friendly
                            <button
                              type="button"
                              className={styles.InfoBtn}
                              onClick={() => setEnvironmentallyModal(true)}
                            >
                              <Icon icon="icon-info" size={14} />
                            </button>
                          </P2>
                          <div className={styles.Scale}>
                            {[...Array(5)].map((_, index) => (
                              <span
                                key={`scale-${index}`}
                                className={environmentally.value >= index + 1 ? styles.Active : ''}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {text && <P1 className="rte" nestedStyles dangerouslySetInnerHTML={{ __html: text }} />}
                  </div>
                </div>

                {didYouKnow.length ? <KnowPointsCard didYouKnow={didYouKnow} /> : null}
              </animated.div>
            )
        )}
      </Card>
    </>
  )
}

Highlights.propTypes = {
  title: PropTypes.string,
  goodFor: PropTypes.string,
  time: PropTypes.string,
  earnings: PropTypes.string,
  defaultRate: PropTypes.string,
  liquidity: PropTypes.shape({
    value: PropTypes.string,
    title: PropTypes.string,
    text: PropTypes.string
  }),
  diversification: PropTypes.shape({
    value: PropTypes.number,
    title: PropTypes.string,
    text: PropTypes.string
  }),
  environmentally: PropTypes.shape({
    value: PropTypes.number,
    title: PropTypes.string,
    text: PropTypes.string
  }),
  text: PropTypes.string,
  avgBlock: PropTypes.shape({
    image: PropTypes.string,
    isEnabled: PropTypes.bool,
    text: PropTypes.string,
    value: PropTypes.string
  }),
  didYouKnow: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string
    })
  )
}

Highlights.defaultProps = {
  title: '',
  goodFor: '',
  time: '',
  earnings: '',
  defaultRate: '',
  liquidity: {
    value: '',
    title: '',
    text: ''
  },
  diversification: {
    value: null,
    title: '',
    text: ''
  },
  environmentally: {
    value: null,
    title: '',
    text: ''
  },
  text: '',
  avgBlock: {
    image: '',
    isEnabled: false,
    text: '',
    value: ''
  },
  didYouKnow: []
}

export default Highlights
